import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  LayoutDashboard,
  BarChart3,
  CalendarDays,
  Grid2X2,
  CheckSquare,
  FileSignature,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Sun,
  Moon,
  Sparkles,
  Send,
  X,
  Mic,
  MicOff,
  Volume2,
  UserCircle,
} from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { chatClientAi, getClientApprovals, speakClientAi } from '../../api/clientPortal';

const GOLD = '#C9A84C';

const DOCK_H = 64;
const ICON_SIZE = 42;
const ICON_GAP = 8;

const NAV_ITEMS = [
  { label: 'Overview',   icon: LayoutDashboard, path: '/client-portal/dashboard' },
  { label: 'Collection', icon: Grid2X2,         path: '/client-portal/collection' },
  { label: 'Performance', icon: BarChart3,      path: '/client-portal/performance' },
  { label: 'Drops',      icon: CalendarDays,    path: '/client-portal/drops' },
  { label: 'Approvals',  icon: CheckSquare,     path: '/client-portal/approvals', badge: true },
  { label: 'Launch',     icon: FileSignature,   path: '/client-portal/agreement' },
  { label: 'Account',    icon: UserCircle,      path: '/client-portal/account' },
];

export default function ClientLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clientBrand, clientBrands, logout, selectClientBrand } = useClientAuth();
  const { theme: T, themeId, setThemeId } = useClientTheme();
  const isLight = themeId === 'light';
  const [pendingCount, setPendingCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);

  useEffect(() => {
    let active = true;
    getClientApprovals('pending')
      .then((data) => { if (active) setPendingCount(Array.isArray(data?.approvals) ? data.approvals.length : 0); })
      .catch(() => {});
    return () => { active = false; };
  }, [location.pathname, clientBrand?.id, clientBrand?._id]);

  useEffect(() => {
    const brandSessionId = clientBrand?._id || clientBrand?.id || clientBrand?.slug;
    if (!brandSessionId) return;
    const sessionKey = `studioSivadPortalWelcome:${brandSessionId}`;
    if (sessionStorage.getItem(sessionKey) === 'shown') return;
    sessionStorage.setItem(sessionKey, 'shown');
    setAssistantOpen(true);
    setAssistantLoading(true);
    chatClientAi({ mode: 'welcome' })
      .then((data) => {
        setAssistantMessages([{ role: 'assistant', ...(data?.reply || {}) }]);
      })
      .catch(() => {
        setAssistantMessages([{
          role: 'assistant',
          answer: `Welcome back${clientBrand?.name ? ` to ${clientBrand.name}` : ''}. I can help explain approvals, launch readiness, drop performance, and what needs attention.`,
          actions: [{ label: 'Open Overview', path: '/client-portal/dashboard' }],
          suggestedQuestions: ['What needs my attention?', 'Are we ready to launch?', 'Which product is performing best?'],
          sourceContext: { used: ['portal data'] },
        }]);
      })
      .finally(() => setAssistantLoading(false));
  }, [clientBrand?._id, clientBrand?.id, clientBrand?.slug, clientBrand?.name]);

  const sendAssistantMessage = async (message = assistantInput) => {
    const cleanMessage = String(message || '').trim();
    if (!cleanMessage || assistantLoading) return;
    setAssistantInput('');
    setAssistantMessages((prev) => [...prev, { role: 'user', answer: cleanMessage }]);
    setAssistantLoading(true);
    try {
      const data = await chatClientAi({ mode: 'chat', message: cleanMessage });
      setAssistantMessages((prev) => [...prev, { role: 'assistant', ...(data?.reply || {}) }]);
    } catch (error) {
      setAssistantMessages((prev) => [...prev, {
        role: 'assistant',
        answer: `I am having trouble refreshing the live portal data right now, but I can still route you to the right place. For approvals, open Approvals. For sales, traffic, checkout activity, and product performance, open Performance. For current drop status and history, open Drops.`,
        actions: [
          { label: 'Open Approvals', path: '/client-portal/approvals' },
          { label: 'Open Performance', path: '/client-portal/performance' },
          { label: 'Open Drops', path: '/client-portal/drops' },
        ],
        suggestedQuestions: ['What needs my attention?', 'Show performance', 'Open current drop'],
      }]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const drawerWidth = 200;
  const activeBrandId = clientBrand?.id || clientBrand?._id || '';
  const hasMultipleBrands = Array.isArray(clientBrands) && clientBrands.length > 1;

  useEffect(() => {
    if (!activeBrandId) return;
    queryClient.invalidateQueries();
  }, [activeBrandId, queryClient]);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: 'inherit', paddingBottom: DOCK_H + 12 }}>
      <style>{`
        .client-portal-main {
          position: relative;
          z-index: 1;
          width: calc(100vw - 392px);
          max-width: 980px;
          margin: 0 auto 0 32px;
          padding: 32px 24px 12px;
        }

        .client-portal-main--collection {
          position: relative;
          z-index: 1;
          width: calc(100vw - 360px);
          max-width: none;
          margin: 0 auto 0 0;
          padding: 0;
        }

        .client-portal-dock {
          left: 32px;
          width: min(980px, calc(100vw - 392px));
          border-radius: 16px;
          border-left: 1px solid transparent;
        }

        .client-portal-dock-mask {
          right: 360px;
        }

        @media (max-width: 980px) {
          .client-portal-main,
          .client-portal-main--collection {
            width: 100%;
            max-width: none;
            margin: 0;
            padding: 24px 18px 96px;
          }

          .client-portal-main--collection {
            padding: 0;
          }

          .client-portal-watermark {
            left: 0;
            right: 0;
            width: 100%;
          }

          .client-portal-dock {
            left: 8px;
            right: 8px;
            width: auto;
            border-radius: 16px;
          }

          .client-portal-dock-mask {
            right: 0;
          }

          .client-portal-ai-lane {
            display: none;
          }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: 18,
          left: 32,
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          borderRadius: 12,
          border: `1px solid ${T.border}`,
          background: isLight ? 'rgba(248,250,252,0.84)' : 'rgba(14,14,16,0.72)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: isLight ? '0 12px 28px rgba(15,23,42,0.08)' : '0 12px 28px rgba(0,0,0,0.28)',
        }}
      >
        <span style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: T.textSub,
        }}>
          Viewing
        </span>
        {hasMultipleBrands ? (
          <select
            value={String(activeBrandId || '')}
            onChange={(event) => {
              const changed = selectClientBrand(event.target.value);
              if (changed) {
                sessionStorage.removeItem(`studioSivadPortalWelcome:${event.target.value}`);
                navigate(location.pathname, { replace: true });
              }
            }}
            style={{
              appearance: 'none',
              border: `1px solid ${isLight ? 'rgba(100,116,139,0.28)' : 'rgba(255,255,255,0.14)'}`,
              background: isLight ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.07)',
              color: T.text,
              borderRadius: 9,
              padding: '6px 28px 6px 10px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {clientBrands.map((brand) => (
              <option key={String(brand.id || brand._id)} value={String(brand.id || brand._id)}>
                {brand.name || brand.slug || 'Brand'}
              </option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 800, color: T.text }}>
            {clientBrand?.name || 'Brand'}
          </span>
        )}
      </div>

      {/* Brand name watermark — centered on the left content lane */}
      {clientBrand?.name && (
        <div
          className="client-portal-watermark"
          style={{
            position: 'fixed',
            top: 0,
            left: 32,
            bottom: DOCK_H,
            width: 'min(980px, calc(100vw - 392px))',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: 0,
            overflow: 'hidden',
          }}
        >
          <div style={{
            fontSize: 'clamp(52px, 11vw, 130px)',
            fontWeight: 900,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.025)',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            lineHeight: 1,
          }}>
            {clientBrand.name}
          </div>
        </div>
      )}

      {/* Page content */}
      <main className={location.pathname === '/client-portal/collection' ? 'client-portal-main--collection' : 'client-portal-main'}>
        <Outlet key={String(activeBrandId || 'brand')} />
      </main>

      <aside
        className="client-portal-ai-lane"
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 360,
          borderLeft: `1px solid ${T.border}`,
          background: isLight
            ? 'linear-gradient(180deg, rgba(248,250,252,0.88), rgba(241,245,249,0.96))'
            : 'linear-gradient(180deg, rgba(14,14,16,0.72), rgba(9,9,10,0.94))',
          boxShadow: isLight
            ? 'inset 1px 0 0 rgba(255,255,255,0.72)'
            : 'inset 1px 0 0 rgba(255,255,255,0.035)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      <PortalAssistant
        open={assistantOpen}
        setOpen={setAssistantOpen}
        messages={assistantMessages}
        loading={assistantLoading}
        input={assistantInput}
        setInput={setAssistantInput}
        sendMessage={sendAssistantMessage}
        navigate={navigate}
        T={T}
        isLight={isLight}
      />

      <div
        aria-hidden="true"
        className="client-portal-dock-mask"
        style={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          height: DOCK_H + 18,
          background: T.bg,
          pointerEvents: 'none',
          zIndex: 9997,
        }}
      />

      {/* Dock — fixed bottom */}
      <div
        className="client-portal-dock"
        style={{
          position: 'fixed',
          bottom: 8,
          height: DOCK_H,
          background: T.dockBg,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: T.dockBorder,
          boxShadow: isLight
            ? '0 4px 24px rgba(55,65,81,0.18)'
            : '0 4px 24px rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Dock label — matches OS dock style exactly */}
        <div
          style={{
            width: 190,
            minWidth: 120,
            flexShrink: 0,
            overflow: 'hidden',
            paddingLeft: 24,
            display: 'flex',
            alignItems: 'center',
            userSelect: 'none',
          }}
        >
          <span style={{
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: T.dockBrandText || GOLD,
            whiteSpace: 'nowrap',
          }}>
            Studio Sivad
          </span>
        </div>

        {/* Icons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: ICON_GAP, flex: '1 1 auto', minWidth: 0 }}>
          {NAV_ITEMS.map(({ label, icon: Icon, path, badge }) => {
            const active = isActive(path);
            return (
              <DockIcon
                key={path}
                label={label}
                icon={Icon}
                active={active}
                badge={badge && pendingCount > 0 ? pendingCount : 0}
                onClick={() => navigate(path)}
                T={T}
                isLight={isLight}
              />
            );
          })}
        </div>

        {/* Right-side drawer */}
        <div
          style={{
            position: 'absolute',
            right: drawerOpen ? -(drawerWidth + 6) : -18,
            top: '50%',
            transform: 'translateY(-50%)',
            height: 48,
            width: drawerOpen ? drawerWidth : 14,
            borderRadius: '0 10px 10px 0',
            border: drawerOpen ? T.dockBorder : `1px solid ${isLight ? 'rgba(100,116,139,0.2)' : 'rgba(120,118,112,0.22)'}`,
            borderLeft: 'none',
            background: drawerOpen ? T.dockBg : (isLight ? 'rgba(150,146,138,0.22)' : 'rgba(120,118,112,0.18)'),
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: drawerOpen ? 'space-between' : 'center',
            padding: drawerOpen ? '0 8px 0 12px' : 0,
            transition: 'right 0.2s ease, width 0.2s ease, padding 0.2s ease',
            overflow: 'visible',
          }}
        >
          {drawerOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Theme toggle */}
              {[
                { id: 'light', Icon: Sun },
                { id: 'dark', Icon: Moon },
              ].map(({ id, Icon }) => (
                <button
                  key={id}
                  onClick={() => setThemeId(id)}
                  style={{
                    width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    border: themeId === id ? `1px solid ${GOLD}` : `1px solid ${isLight ? 'rgba(100,116,139,0.3)' : 'rgba(255,255,255,0.16)'}`,
                    background: themeId === id ? 'rgba(201,168,76,0.15)' : (isLight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)'),
                    color: themeId === id ? GOLD : T.textSub,
                  }}
                >
                  <Icon size={13} strokeWidth={2} />
                </button>
              ))}

              <div style={{ width: 1, height: 20, background: T.border, margin: '0 2px' }} />

              {/* Sign out */}
              <button
                onClick={() => { logout(); navigate('/client-portal/login'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 7, border: `1px solid ${isLight ? 'rgba(100,116,139,0.28)' : 'rgba(255,255,255,0.12)'}`,
                  background: isLight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.05)',
                  color: T.textSub, fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                }}
              >
                <LogOut size={12} />
                Sign out
              </button>
            </div>
          )}

          {/* Toggle button */}
          <button
            onClick={() => setDrawerOpen((v) => !v)}
            style={{
              width: drawerOpen ? 22 : 10,
              height: drawerOpen ? 22 : 32,
              borderRadius: drawerOpen ? 6 : 4,
              border: drawerOpen ? `1px solid ${isLight ? 'rgba(100,116,139,0.24)' : 'rgba(255,255,255,0.12)'}` : 'none',
              background: drawerOpen
                ? (isLight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.07)')
                : (isLight ? 'rgba(56,52,48,0.18)' : 'rgba(220,220,220,0.14)'),
              color: T.textSub,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            {drawerOpen ? <ChevronRight size={12} /> : <ChevronLeft size={9} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function PortalAssistant({ open, setOpen, messages, loading, input, setInput, sendMessage, navigate, T, isLight }) {
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const voiceReplyPendingRef = useRef(false);
  const welcomePlayedRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [welcomeReady, setWelcomeReady] = useState(false);
  const latestAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
  const latestActions = Array.isArray(latestAssistant?.actions) ? latestAssistant.actions.filter((action) => action?.path || action?.label) : [];

  useEffect(() => () => {
    recognitionRef.current?.abort?.();
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
    }
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
    setAudioPlaying(false);
  }, []);

  const playAssistantAudio = useCallback(async (text = latestAssistant?.answer) => {
    const cleanText = String(text || '').trim();
    if (!cleanText || audioLoading) return;
    setVoiceError('');
    setAudioLoading(true);
    stopAudio();
    try {
      const blob = await speakClientAi(cleanText, { voice: 'client_portal' });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onplay = () => setAudioPlaying(true);
      audio.onended = () => {
        setAudioPlaying(false);
        URL.revokeObjectURL(audioUrl);
        if (audioRef.current === audio) audioRef.current = null;
      };
      audio.onerror = () => {
        setAudioPlaying(false);
        setVoiceError('The voice response could not play. The written answer is still available below.');
      };
      await audio.play();
    } catch (error) {
      setVoiceError(`Voice unavailable: ${error?.message || 'ElevenLabs could not generate audio'}. The written answer is still available below.`);
    } finally {
      setAudioLoading(false);
      setWelcomeReady(false);
    }
  }, [audioLoading, latestAssistant?.answer, stopAudio]);

  useEffect(() => {
    if (loading || !latestAssistant?.answer) return;
    if (!welcomePlayedRef.current && messages.length === 1 && latestAssistant.role === 'assistant') {
      welcomePlayedRef.current = true;
      setWelcomeReady(true);
      return;
    }
    if (!voiceReplyPendingRef.current) return;
    voiceReplyPendingRef.current = false;
    playAssistantAudio(latestAssistant.answer);
  }, [loading, latestAssistant, latestAssistant?.answer, messages.length, playAssistantAudio]);

  const playReadyUpdate = () => {
    if (!latestAssistant?.answer) {
      setOpen(true);
      return;
    }
    setOpen(true);
    playAssistantAudio(latestAssistant.answer);
  };

  const stopVoice = () => {
    recognitionRef.current?.abort?.();
    setListening(false);
    stopAudio();
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError('Voice input is not supported in this browser. You can still type your question below.');
      return;
    }
    setVoiceError('');
    stopVoice();
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onstart = () => setListening(true);
    recognition.onerror = () => {
      setListening(false);
      setVoiceError('I could not hear that clearly. Tap the mic and try again.');
    };
    recognition.onend = () => setListening(false);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results || [])
        .map((result) => result?.[0]?.transcript || '')
        .join(' ')
        .trim();
      setListening(false);
      if (transcript) {
        voiceReplyPendingRef.current = true;
        sendMessage(transcript);
      }
    };
    recognition.start();
  };

  const assistantStatus = listening
    ? 'Listening...'
    : audioLoading
      ? 'Preparing voice...'
      : audioPlaying
        ? 'Speaking...'
        : loading
          ? 'Reading portal data...'
          : 'Tap to talk';

  return (
    <>
      <style>{`
        @keyframes studioSivadAssistantPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }

        .client-portal-assistant-rail {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 82px;
          width: 360px;
          box-sizing: border-box;
          padding: 28px;
          z-index: 10000;
          display: grid;
          grid-template-rows: minmax(220px, 1fr) auto auto auto;
          align-content: stretch;
          justify-items: center;
          gap: 12px;
          pointer-events: none;
          overflow: hidden;
        }

        .client-portal-assistant-closed {
          right: 180px;
          bottom: 50%;
          transform: translate(50%, 50%);
        }

        .client-portal-assistant-update {
          right: 180px;
          bottom: calc(50% - 48px);
          transform: translateX(50%);
        }

        @media (max-width: 980px) {
          .client-portal-assistant-rail {
            top: auto;
            right: 18px;
            bottom: 86px;
            width: min(320px, calc(100vw - 36px));
            padding: 0;
            max-height: calc(100vh - 110px);
            grid-template-rows: minmax(150px, 1fr) auto auto auto;
            align-content: end;
            justify-items: end;
          }

          .client-portal-assistant-closed {
            right: 16px;
            bottom: 86px;
            transform: none;
          }

          .client-portal-assistant-update {
            right: 76px;
            bottom: 94px;
            transform: none;
          }
        }
      `}</style>
      {!open ? (
        <button
          type="button"
          onClick={welcomeReady ? playReadyUpdate : () => setOpen(true)}
          className="client-portal-assistant-closed"
          style={{
            position: 'fixed',
            width: 50,
            height: 50,
            borderRadius: '50%',
            border: `1px solid ${T.gold}`,
            background: T.id === 'light' ? '#111827' : 'rgba(201,168,76,0.18)',
            color: T.id === 'light' ? '#fff' : T.gold,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            zIndex: 9998,
            boxShadow: welcomeReady
              ? '0 0 0 8px rgba(201,168,76,0.16), 0 0 0 18px rgba(201,168,76,0.07), 0 16px 34px rgba(0,0,0,0.34)'
              : (isLight ? '0 10px 24px rgba(15,23,42,0.24)' : '0 10px 24px rgba(0,0,0,0.48)'),
            animation: welcomeReady ? 'studioSivadAssistantPulse 1.65s ease-in-out infinite' : 'none',
          }}
          aria-label={welcomeReady ? 'Play Studio update' : 'Open Studio assistant'}
        >
          {welcomeReady ? <Volume2 size={19} /> : <Sparkles size={19} />}
        </button>
      ) : null}
      {welcomeReady && !open ? (
        <button
          type="button"
          onClick={playReadyUpdate}
          className="client-portal-assistant-update"
          style={{
            position: 'fixed',
            border: `1px solid ${T.gold}`,
            background: isLight ? 'rgba(255,255,255,0.96)' : 'rgba(18,18,18,0.94)',
            color: T.id === 'light' ? '#111827' : T.gold,
            borderRadius: 999,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 850,
            cursor: 'pointer',
            zIndex: 9998,
            boxShadow: '0 12px 26px rgba(0,0,0,0.20)',
          }}
        >
          Studio update ready
        </button>
      ) : null}
      {open ? (
        <div className="client-portal-assistant-rail">
          <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden', border: `1px solid ${T.border}`, background: isLight ? 'rgba(255,255,255,0.58)' : 'rgba(255,255,255,0.035)', borderRadius: 10, boxShadow: isLight ? 'inset 0 1px 0 rgba(255,255,255,0.82)' : 'inset 0 1px 0 rgba(255,255,255,0.04)', pointerEvents: 'auto', display: 'grid', gridTemplateRows: 'minmax(0, 1fr) auto' }}>
            <div style={{ minHeight: 0, overflowY: 'auto', display: 'grid', alignContent: 'start', gap: 10, padding: 12 }}>
              {messages.map((message, index) => (
                <AssistantBubble key={`${message.role}-${index}`} message={message} navigate={navigate} T={T} showActions={false} />
              ))}
              {loading ? <div style={{ fontSize: 13, color: T.textMuted }}>Reading portal data...</div> : null}
            </div>
            <form
              onSubmit={(event) => { event.preventDefault(); sendMessage(); }}
              style={{ borderTop: `1px solid ${T.border}`, padding: 10, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 36px', gap: 8 }}
            >
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Type fallback"
                style={{ border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, borderRadius: 8, padding: '8px 9px', fontSize: 13, outline: 'none', minWidth: 0 }}
              />
              <button type="submit" disabled={loading || !input.trim()} style={{ border: `1px solid ${T.gold}`, background: T.gold, color: '#101010', borderRadius: 8, display: 'grid', placeItems: 'center', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.62 : 1 }}>
                <Send size={14} />
              </button>
            </form>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', pointerEvents: 'auto' }}>
            <button onClick={() => playAssistantAudio()} disabled={!latestAssistant?.answer || audioLoading} style={{ border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.textSub, borderRadius: 999, height: 34, padding: '0 12px', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: !latestAssistant?.answer || audioLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 800, opacity: !latestAssistant?.answer || audioLoading ? 0.62 : 1 }} aria-label="Play latest assistant answer">
              <Volume2 size={14} />
              Replay
            </button>
            <button onClick={() => { stopVoice(); setOpen(false); }} style={{ border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.textSub, borderRadius: 999, width: 34, height: 34, display: 'grid', placeItems: 'center', cursor: 'pointer' }} aria-label="Close assistant">
              <X size={16} />
            </button>
          </div>

          <div style={{ display: 'grid', justifyItems: 'center', gap: 8, pointerEvents: 'auto' }}>
            <button
              type="button"
              onClick={listening ? stopVoice : startListening}
              disabled={loading}
              style={{
                width: 132,
                height: 132,
                flexShrink: 0,
                borderRadius: '50%',
                border: `1px solid ${listening ? '#ef4444' : audioPlaying ? T.gold : 'rgba(201,168,76,0.58)'}`,
                background: listening
                  ? 'radial-gradient(circle, rgba(239,68,68,0.24), rgba(239,68,68,0.06))'
                  : (isLight ? 'radial-gradient(circle, #111827, #020617)' : 'radial-gradient(circle, rgba(201,168,76,0.28), rgba(201,168,76,0.05))'),
                color: listening ? '#ef4444' : (isLight ? '#fff' : T.gold),
                display: 'grid',
                placeItems: 'center',
                cursor: loading ? 'wait' : 'pointer',
                boxShadow: listening
                  ? '0 0 0 12px rgba(239,68,68,0.10), 0 20px 46px rgba(0,0,0,0.28)'
                  : audioPlaying
                    ? '0 0 0 12px rgba(201,168,76,0.10), 0 20px 46px rgba(0,0,0,0.30)'
                    : '0 18px 46px rgba(0,0,0,0.28)',
                opacity: loading ? 0.68 : 1,
              }}
              aria-label={listening ? 'Stop listening' : 'Start listening'}
            >
              {listening ? <MicOff size={42} /> : <Mic size={42} />}
            </button>
            <div style={{ border: `1px solid ${T.inputBorder}`, background: isLight ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.045)', color: T.text, borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 850 }}>
              {assistantStatus}
            </div>
          </div>

          {voiceError ? <div style={{ maxWidth: 280, border: '1px solid rgba(239,68,68,0.32)', background: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.05)', color: '#ef4444', borderRadius: 10, padding: '8px 10px', fontSize: 12, lineHeight: 1.4, pointerEvents: 'auto' }}>{voiceError}</div> : null}

          {latestActions.length ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', pointerEvents: 'auto' }}>
              {latestActions.slice(0, 3).map((action) => (
                <button
                  key={`${action.label}-${action.path}`}
                  onClick={() => { stopVoice(); setOpen(false); navigate(action.path || '/client-portal/dashboard'); }}
                  style={{ border: `1px solid ${T.gold}`, background: isLight ? 'rgba(255,255,255,0.94)' : 'rgba(15,15,15,0.94)', color: T.id === 'light' ? '#111827' : T.gold, borderRadius: 999, padding: '8px 12px', fontSize: 12, fontWeight: 850, cursor: 'pointer', boxShadow: '0 10px 24px rgba(0,0,0,0.16)' }}
                >
                  {action.label || 'Open'}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function AssistantBubble({ message, navigate, T, showActions = true }) {
  const isUser = message.role === 'user';
  const actions = Array.isArray(message.actions) ? message.actions : [];
  const used = Array.isArray(message.sourceContext?.used) ? message.sourceContext.used : [];
  return (
    <div style={{ justifySelf: isUser ? 'end' : 'start', maxWidth: isUser ? '82%' : '100%' }}>
      <div style={{ border: `1px solid ${isUser ? 'transparent' : T.border}`, background: isUser ? T.gold : (T.id === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)'), color: isUser ? '#101010' : T.textSub, borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.55 }}>
        {message.answer || ''}
      </div>
      {showActions && !isUser && actions.length ? (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
          {actions.map((action) => (
            <button
              key={`${action.label}-${action.path}`}
              onClick={() => navigate(action.path || '/client-portal/dashboard')}
              style={{ border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.textSub, borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
            >
              {action.label || 'Open'}
            </button>
          ))}
        </div>
      ) : null}
      {!isUser && used.length ? (
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 7 }}>
          Based on {used.join(', ')}.
        </div>
      ) : null}
    </div>
  );
}

function DockIcon({ label, icon: Icon, active, badge, onClick, T, isLight }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const getTransform = () => {
    if (pressed) return 'scale(1.05) translateY(-2px)';
    if (hovered) return 'scale(1.2) translateY(-6px)';
    return 'scale(1) translateY(0)';
  };

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Tooltip */}
      {hovered && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)',
          background: isLight ? 'rgba(15,23,42,0.9)' : 'rgba(30,30,30,0.96)',
          color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 600,
          whiteSpace: 'nowrap', pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          zIndex: 1,
        }}>
          {label}
        </div>
      )}

      <button
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setPressed(false); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{
          width: ICON_SIZE, height: ICON_SIZE, borderRadius: '50%',
          background: T.iconBg,
          border: active ? `1.5px solid ${GOLD}` : '1px solid rgba(255,255,255,0.0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          transform: getTransform(),
          transition: 'transform 0.15s ease',
          boxShadow: isLight
            ? '0 2px 8px rgba(55,65,81,0.22), inset 0 1px 0 rgba(255,255,255,0.8)'
            : '0 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
          position: 'relative',
        }}
      >
        <Icon size={Math.round(ICON_SIZE * 0.42)} color={active ? GOLD : T.iconColor} strokeWidth={active ? 2.2 : 1.6} />

        {/* Badge */}
        {badge > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            background: GOLD, color: '#0e0e0e',
            borderRadius: 999, fontSize: 9, fontWeight: 700,
            minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', border: '1.5px solid ' + T.bg,
          }}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>

      {/* Active dot */}
      <div style={{
        marginTop: 4, width: 4, height: 4, borderRadius: '50%',
        background: GOLD, opacity: active ? 1 : 0, transition: 'opacity 0.15s',
      }} />
    </div>
  );
}
